import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  pickRunningHubHistoryListImageSrc,
  type RunningHubHistoryListImageFields,
} from '../utils/runningHubHistoryListImage';

type Props = {
  item: RunningHubHistoryListImageFields;
  /** 传给 <img>，需含尺寸与 object-cover；组件会 absolute inset-0 铺满父级 */
  imgClassName?: string;
};

/**
 * RunningHub 侧栏历史列表预览：thumb COS 优先，无 thumb 时回退结果 URL；回退图加载前 blur。
 */
export const RunningHubHistoryListThumb: React.FC<Props> = ({
  item,
  imgClassName = 'w-full h-full object-cover',
}) => {
  const source = useMemo(() => pickRunningHubHistoryListImageSrc(item), [item]);
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [source?.url]);

  if (!source || failed) {
    return <div className="absolute inset-0 animate-pulse bg-white/10" aria-hidden />;
  }

  const useBlurTransition = source.kind === 'fallback';

  return (
    <>
      {useBlurTransition && !loaded && (
        <div className="absolute inset-0 z-0 animate-pulse bg-white/10" aria-hidden />
      )}
      <img
        ref={imgRef}
        src={source.url}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        className={[
          'absolute inset-0 z-[1]',
          imgClassName,
          useBlurTransition ? 'transition-[filter,opacity,transform] duration-300 ease-out' : '',
          useBlurTransition && !loaded ? 'blur-md opacity-50 scale-[1.02]' : '',
          useBlurTransition && loaded ? 'blur-0 opacity-100 scale-100' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      />
    </>
  );
};
